import React, { useState, useEffect } from 'react'
import styles from './style'
import propTypes from './prop'
import {
  withStyles,
  FormControl,
  FormGroup,
  Switch,
  FormControlLabel,
  InputAdornment,
  Typography,
} from '@material-ui/core'

import { AssignmentFieldNames } from 'src/model/assignment'
import {
  QuestionFieldNames,
  CardSettingsTypes,
  CardSettingBuilder,
} from 'src/model/card'
import { AnswerItemType } from 'src/model/card'
import { CoreButton } from 'src/components/core'
import { useDispatch, useSelector } from 'react-redux'
import { messageBoxHideAction } from 'src/reducers/message-box/action'
import {
  useCardSettings,
  patchSetting,
  createSetting,
} from 'src/hooks/useCardSettings'
import { Icon } from '@iconify/react'
import { CoreIconButton, CoreTextField } from 'src/components/core'
import SunEditor from 'suneditor-react'
import 'suneditor/dist/css/suneditor.min.css'
import { mediaImageUploadAction } from 'src/reducers/media-image/action'

const CardSettings = props => {
  const { classes, cardId, closeThisModal } = props
  const dispatch = useDispatch()
  const entity = useSelector(state => state.card.currentCard)
  const [maskedInputs, setMaskedInputs] = useState({})
  const settingsSchema = CardSettingBuilder(
    entity[QuestionFieldNames.QUESTION_TYPE],
  )
  const baseSettings =
    CardSettingsTypes[entity[QuestionFieldNames.QUESTION_TYPE]]

  const cardBaseSettings = useCardSettings('cardSettings')

  const [localSettings, setLocalSettings] = useState(
    cardBaseSettings ? JSON.parse(cardBaseSettings.value) : settingsSchema,
  )

  useEffect(() => {
    if (!cardBaseSettings) {
      createSetting(
        dispatch,
        cardId,
        'cardSettings',
        JSON.stringify(settingsSchema),
        () => {},
      )
    }
  }, [cardBaseSettings])

  useEffect(() => {
    if (cardBaseSettings) {
      setLocalSettings(JSON.parse(cardBaseSettings.value))
    }
  }, [cardBaseSettings])

  const handleSettingChange = (key, newValue) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: newValue,
    }))

    // If not test or multiple choice, save immediately
    if (
      !(
        entity.type === AnswerItemType.TESTINGSINGLECHOICE ||
        entity.type === AnswerItemType.TESTINGMULTIPLECHOICE
      )
    ) {
      patchSetting(
        dispatch,
        cardId,
        cardBaseSettings.id,
        'cardSettings',
        JSON.stringify({
          ...localSettings,
          [key]: newValue,
        }),
        () => {},
      )
    }
  }

  const handleSaveSettings = () => {
    patchSetting(
      dispatch,
      cardId,
      cardBaseSettings.id,
      'cardSettings',
      JSON.stringify(localSettings),
      () => {
        dispatch(messageBoxHideAction())
      },
    )
  }
  return (
    <FormControl className={classes.form}>
      {baseSettings &&
        baseSettings.settings.map((value, index) => {
          let domContent = <></>

          switch (value.type) {
            case 'string':
              domContent = (
                <CoreTextField
                  key={index}
                  label={value.label}
                  value={localSettings[value.key]}
                  onChange={e => handleSettingChange(value.key, e.target.value)}
                  disabled={!localSettings}
                  fullWidth
                />
              )
              break
            case 'password':
              domContent = (
                <CoreTextField
                  key={index}
                  type={maskedInputs[index] ? 'text' : 'password'}
                  label={value.label}
                  value={localSettings[value.key]}
                  onChange={e => handleSettingChange(value.key, e.target.value)}
                  disabled={!localSettings}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CoreIconButton
                          aria-label="toggle password visibility"
                          onClick={() =>
                            setMaskedInputs({
                              ...maskedInputs,
                              [index]: !maskedInputs[index],
                            })
                          }
                          edge="end"
                          variant="text"
                          color="dark"
                        >
                          {maskedInputs[index] ? (
                            <Icon icon="mdi:eye" />
                          ) : (
                            <Icon icon="mdi:eye-off" />
                          )}
                        </CoreIconButton>
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />
              )
              break
            case 'boolean':
              domContent = (
                <div className={classes.dom}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={localSettings[value.key]}
                          onChange={e =>
                            handleSettingChange(value.key, e.target.checked)
                          }
                        />
                      }
                      disabled={!localSettings}
                      label={value.label}
                    />
                  </FormGroup>
                </div>
              )
              break
            case 'number':
              domContent = (
                <CoreTextField
                  type="number"
                  key={index}
                  label={value.label}
                  value={localSettings[value.key]}
                  onChange={e => handleSettingChange(value.key, e.target.value)}
                  disabled={!localSettings}
                  fullWidth
                />
              )
              break
            case 'editor':
              domContent = (
                <div className={classes.editorContainer}>
                  <Typography
                    variant="caption"
                    fontSize="12px"
                    className={classes.label}
                  >
                    {value.label}
                  </Typography>
                  <SunEditor
                    key={'editor'}
                    value={localSettings[value.key]}
                    disabled={!localSettings['showAnswerAfterSelect']}
                    readOnly={!localSettings['showAnswerAfterSelect']}
                    onBlur={(event, editorContents) => {
                      handleSettingChange(value.key, editorContents)
                    }}
                    defaultValue={localSettings[value.key] || ''}
                    placeholder={value.placeholder}
                    className={classes.textArea}
                    setOptions={{
                      mode: 'inline',
                      formats: ['h1', 'h2', 'p'],
                      placeholder:
                        'This will be shown to the user after they answer the question.',
                      height: 100,
                      buttonList: [
                        [],
                        ['formatBlock'],
                        [],
                        ['bold'],
                        [],
                        ['codeView'],
                        [],
                        [
                          ':i-More Misc-default.more_vertical',
                          'fontColor',
                          'hiliteColor',
                          'align',
                          'link',
                          'italic',
                          'underline',
                          'removeFormat',
                        ],
                      ],
                      pasteTagsWhitelist: 'p|h1|h2|h3',
                      popupDisplay: 'local',
                    }}
                  />
                </div>
              )
              break
            default:
              break
          }

          return (
            <div className={classes.action} key={index}>
              {domContent}
            </div>
          )
        })}
      {(entity.type == AnswerItemType.TESTINGSINGLECHOICE ||
        entity.type == AnswerItemType.TESTINGMULTIPLECHOICE) && (
        <div className={classes.saveButton}>
          <CoreButton onClick={handleSaveSettings}>Save Settings</CoreButton>
        </div>
      )}
    </FormControl>
  )
}

CardSettings.propTypes = propTypes

export default withStyles(styles)(CardSettings)
